import { cloneDeep } from 'lodash-es';

export abstract class DateTimeInterval {
    public static readonly HOUR: string = 'HOUR';
    public static readonly DAY: string = 'DAY';
    public static readonly WEEK: string = 'WEEK';
    public static readonly MONTH: string = 'MONTH';
}

export abstract class ProtocolConstant {
    public static readonly PROTOCOL_LIST: Array<string> = ['TCP', 'UDP', 'ICMP'];
}

export abstract class DefaultSearchConstant {
    public static readonly BASE_SEARCH_DTO: string = 'BASE_SEARCH_DTO';
    public static readonly DEFAULT_SEARCH_DTO: string = 'DEFAULT_SEARCH_DTO';
    public static readonly CUSTOM_SEARCH_DTO_WITH_TAGS: string = 'CUSTOM_SEARCH_DTO_WITH_TAGS';

    public static readonly SEARCH_DTOS: Map<string, unknown> = new Map<string, unknown>()
        .set(DefaultSearchConstant.BASE_SEARCH_DTO, { orderBy: [{}] })
        .set(DefaultSearchConstant.DEFAULT_SEARCH_DTO, {
            pageRequest: { pageSize: 25, pageNumber: 0 },
            criteria: {},
        })
        .set(DefaultSearchConstant.CUSTOM_SEARCH_DTO_WITH_TAGS, {
            pageRequest: { pageSize: 25, pageNumber: 0 },
            criteria: { tags: [] as Array<string> },
        });

    public static getDTO(type: string): unknown {
        return cloneDeep(DefaultSearchConstant.SEARCH_DTOS.get(type));
    }
}

export class UserPreferenceConstant {
    //category
    public static readonly CATEGORY_UI: string = 'ui';
    public static readonly CATEGORY_UI_ASSESSMENT: string = 'ui.assessment';
    public static readonly CATEGORY_UI_FINDING: string = 'ui.finding';
    public static readonly CATEGORY_UI_FINDING_ENDPOINT: string = 'ui.finding-endpoint';
    public static readonly CATEGORY_UI_ASSET: string = 'ui.asset';
    public static readonly CATEGORY_UI_EVENT: string = 'ui.event';
    public static readonly CATEGORY_UI_ADMIN: string = 'ui.admin';
    public static readonly CATEGORY_UI_SCAN: string = 'ui.scan';
    public static readonly CATEGORY_UI_ENDPOINT: string = 'ui.endpoint';

    //subCategory
    public static readonly SUBCATEGORY_LAYOUT: string = 'layout';
    public static readonly SUBCATEGORY_QUERIES: string = 'queries';

    //attributeKey
    public static readonly ATTRIBUTEKEY_THEME: string = 'theme';
    public static readonly ATTRIBUTEKEY_ASSESSMENT_SEARCH: string = 'assessment.search';
    public static readonly ATTRIBUTEKEY_TASK_SEARCH: string = 'task.search';
    public static readonly ATTRIBUTEKEY_ASSESSMENT_SEARCH_GRID: string = 'assessment.search.grid';
    public static readonly ATTRIBUTEKEY_ASSESSMENT_SEARCH_COLUMNS: string = 'assessment.search.columns';
    public static readonly ATTRIBUTEKEY_ASSESSMENT_DASHBOARD_NEW_WIDGETS = 'assessment.dashboard.new-widgets';
    public static readonly ATTRIBUTEKEY_TASK_SEARCH_GRID: string = 'task.search.grid';
    public static readonly ATTRIBUTEKEY_TASK_SEARCH_COLUMNS: string = 'task.search.columns';
    public static readonly TASK_SEARCH_TASKS_FOR_NAVIGATION: string = 'task.search.tasks_for_navigation';
    public static readonly TASK_SEARCH_TABLE_SCROLL: string = 'task.search.table_scroll';
    public static readonly ASSESSMENT_SEARCH_TABLE_SCROLL: string = 'assessment.search.table_scroll';
    public static readonly ATTRIBUTEKEY_ASSESSMENT_SEARCH_PAGE_REQUEST: string = 'assessment.search.page-request';
    public static readonly ATTRIBUTEKEY_TASK_SEARCH_PAGE_REQUEST: string = 'task.search.page-request';
    public static readonly ATTRIBUTEKEY_CONTROL_SEARCH_COLUMNS: string = 'control.search.columns';
    public static readonly CONTROL_SEARCH_TABLE_SCROLL: string = 'control.search.table_scroll';
    public static readonly CONTROL_SEARCH_FILTERS: string = 'control.search.filters';
    public static readonly ATTRIBUTEKEY_CONTROL_SEARCH_GRID: string = 'control.search.grid';
    public static readonly ATTRIBUTEKEY_CONTROL_SEARCH_PAGE_REQUEST: string = 'control.search.page-request';
    public static readonly ATTRIBUTEKEY_CONTROL_SEARCH_PAGE_SIZE: string = 'control.search.page-size';
    public static readonly ATTRIBUTEKEY_REFTASK_SEARCH_COLUMNS: string = 'reftask.search.columns';
    public static readonly REFTASK_SEARCH_TABLE_SCROLL: string = 'reftask.search.table_scroll';
    public static readonly ATTRIBUTEKEY_REFTASK_SEARCH_GRID: string = 'reftask.search.grid';
    public static readonly ATTRIBUTEKEY_REFTASK_SEARCH_PAGE_REQUEST: string = 'reftask.search.page-request';
    public static readonly REFERENCE_TASKS_SEARCH_FILTERS: string = 'reference-tasks.search.filters';
    public static readonly ATTRIBUTEKEY_REFTASK_SEARCH_PAGE_SIZE: string = 'reftask.search.page-size';
    public static readonly ATTRIBUTEKEY_FRAMEWORK_SEARCH_COLUMNS: string = 'framework.search.columns';
    public static readonly FRAMEWORK_SEARCH_TABLE_SCROLL: string = 'framework.search.table_scroll';
    public static readonly ATTRIBUTEKEY_FRAMEWORK_SEARCH_GRID: string = 'framework.search.grid';
    public static readonly ATTRIBUTEKEY_FRAMEWORK_SEARCH_PAGE_REQUEST: string = 'framework.search.page-request';
    public static readonly ATTRIBUTEKEY_FRAMEWORK_SEARCH_PAGE_SIZE: string = 'framework.search.page-size';
    public static readonly ATTRIBUTEKEY_FRAMEWORK_COMPONENT_SEARCH_SORT: string = 'framework-component.search.sort';
    public static readonly FRAMEWORK_COMPONENT_SEARCH_TABLE_SCROLL: string = 'framework-component.search.table_scroll';
    public static readonly FRAMEWORK_COMPONENT_SEARCH_FILTERS: string = 'framework-component.search.filters';
    public static readonly ATTRIBUTEKEY_FINDING_SEARCH_PAGE_SIZE: string = 'finding.search.page-size';
    public static readonly ATTRIBUTEKEY_FINDING_ENDPOINT_SEARCH_PAGE_SIZE: string = 'finding-endpoint.search.page-size';
    public static readonly ATTRIBUTEKEY_ASSET_SEARCH_PAGE_SIZE: string = 'asset.search.page-size';
    public static readonly ATTRIBUTEKEY_EVENT_SEARCH_PAGE_SIZE: string = 'event.search.page-size';
    public static readonly ATTRIBUTEKEY_SCAN_ADMIN_SEARCH_PAGE_SIZE: string = 'scan-admin.search.page-size';
    public static readonly ATTRIBUTEKEY_SCAN_SCANNERS_SEARCH_PAGE_SIZE: string = 'scan-scanners.search.page-size';
    public static readonly ATTRIBUTEKEY_SCAN_DISPUTES_SEARCH_PAGE_SIZE: string = 'scan-disputes.search.page-size';
    public static readonly ATTRIBUTEKEY_SCAN_PROFILE_SEARCH_PAGE_SIZE: string = 'scan-profile.search.page-size';
    public static readonly ATTRIBUTEKEY_SCAN_HISTORY_SEARCH_PAGE_SIZE: string = 'scan-history.search.page-size';
    public static readonly ATTRIBUTEKEY_SCAN_APPLIANCE_SEARCH_PAGE_SIZE: string = 'scan-appliance.search.page-size';
    public static readonly ATTRIBUTEKEY_SCAN_RESULTS_SEARCH_PAGE_REQUEST: string = 'scan-results.search.page-request';
    public static readonly ATTRIBUTEKEY_ORGANIZATION_SEARCH_PAGE_SIZE: string = 'organization.search.page-size';
    public static readonly ATTRIBUTEKEY_USER_SEARCH_PAGE_SIZE: string = 'user.search.page-size';
    public static readonly ATTRIBUTEKEY_ROLE_SEARCH_PAGE_SIZE: string = 'role.search.page-size';
    public static readonly ATTRIBUTEKEY_PRIVILEGE_SEARCH_PAGE_SIZE: string = 'privilege.search.page-size';
    public static readonly ATTRIBUTEKEY_ENDPOINT_DEVICE_SEARCH_PAGE_SIZE: string = 'endpoint-device.search.page-size';
    public static readonly ATTRIBUTEKEY_ENDPOINT_FINDING_SEARCH_PAGE_SIZE: string = 'endpoint-finding.search.page-size';
    public static readonly ATTRIBUTEKEY_ASSESSMENT_INDIVIDUAL_DASHBOARD_WIDGETS: string =
        'assessment.individual-dashboard.widgets';
    public static readonly FRAMEWORKS_SEARCH_FILTERS: string = 'frameworks.search.filters';
    public static readonly EVENTS_SEARCH_FILTERS: string = 'events.search.filters';
    public static readonly FINDINGS_SEARCH_FILTERS: string = 'findings.search.filters';
    public static readonly ENDPOINT_FINDINGS_SEARCH_FILTERS: string = 'endpoint-findings.search.filters';
    public static readonly USERS_SEARCH_FILTERS: string = 'users.search.filters';
    public static readonly USERS_SEARCH_COLUMNS: string = 'users.search.columns';
    public static readonly ORGANIZATION_SEARCH_FILTERS: string = 'organization.search.filters';
    public static readonly ORGANIZATION_SEARCH_COLUMNS: string = 'organization.search.columns';
    public static readonly ADMIN_ORDERS_FILTERS: string = 'admin.order.filters';
    public static readonly ATTRIBUTEKEY_AD_TASK_SEARCH_COLUMNS: string = 'ad-task.search.columns';
    public static readonly ATTRIBUTEKEY_ASSESSMENT_SEARCH_MY_ASSESSMENTS_TOGGLE: string =
        'assessment.search.my-assessments-toggle';
    public static readonly ATTRIBUTEKEY_TASK_SEARCH_MY_TASKS_TOGGLE: string = 'task.search.my-tasks-toggle';

    // SCAN-754: sessionStorage keys for vc-scan-ui table state (not durable user preferences)
    public static readonly SCAN_MANAGEMENT_SEARCH_STATE: string = 'scan.management.search.state';
    public static readonly SCAN_MANAGEMENT_SEARCH_TABLE_SCROLL: string = 'scan.management.search.table_scroll';
    public static readonly SCAN_HISTORY_SEARCH_STATE: string = 'scan.history.search.state';
    public static readonly SCAN_HISTORY_SEARCH_TABLE_SCROLL: string = 'scan.history.search.table_scroll';
    public static readonly SCAN_APPLIANCES_SEARCH_STATE: string = 'scan.appliances.search.state';
    public static readonly SCAN_APPLIANCES_SEARCH_TABLE_SCROLL: string = 'scan.appliances.search.table_scroll';
    public static readonly SCAN_SUPPRESSION_RULES_SEARCH_STATE: string = 'scan.suppression-rules.search.state';
    public static readonly SCAN_SUPPRESSION_RULES_SEARCH_TABLE_SCROLL: string =
        'scan.suppression-rules.search.table_scroll';
    public static readonly SCAN_AUTHENTICATION_HUB_SEARCH_STATE: string = 'scan.authentication-hub.search.state';
    public static readonly SCAN_AUTHENTICATION_HUB_SEARCH_TABLE_SCROLL: string =
        'scan.authentication-hub.search.table_scroll';
    public static readonly SCAN_RESULTS_VULNERABILITIES_SEARCH_STATE: string =
        'scan.results.vulnerabilities.search.state';
    public static readonly SCAN_RESULTS_VULNERABILITIES_SEARCH_TABLE_SCROLL: string =
        'scan.results.vulnerabilities.search.table_scroll';

    public static readonly PERSISTED_ASSESSMENT: string = 'persistedAssessment';
    public static readonly PERSISTED_TASK: string = 'persistedTask';
    public static readonly PERSISTED_CONTROL: string = 'persistedControl';
    public static readonly PERSISTED_PAGE_SIZE: string = 'persistedPageSize';
}

export class CoreConstant {
    public static readonly X_AUTH_TOKEN: string = 'X-Auth-Token';
    public static readonly VC_THEME: string = 'VC_THEME';
    public static readonly VC_LANGUAGE_ABBREVIATION: string = 'VC_LANGUAGE_ABBREVIATION';
}
